import React, { useEffect, useMemo, useState } from 'react';
import './settings.css';

const THEME_STORAGE_KEY = 'ocrp-theme-preference';

const getStoredThemePreference = () => {
  if (typeof window === 'undefined') {
    return 'dark';
  }
  const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
  if (stored === 'light' || stored === 'system') {
    return stored;
  }
  return 'dark';
};

const getSystemColorScheme = () => {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return 'dark';
  }
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
};

function Settings() {
  const [activeTab, setActiveTab] = useState('general');
  const [themePreference, setThemePreference] = useState(() => getStoredThemePreference());
  const [systemTheme, setSystemTheme] = useState(() => getSystemColorScheme());

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, themePreference);
    } catch (err) {
      // ignore storage errors
    }
  }, [themePreference]);

  useEffect(() => {
    const media = typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia('(prefers-color-scheme: dark)')
      : null;

    if (!media) {
      setSystemTheme('dark');
      return undefined;
    }

    const updateSystemTheme = (event) => {
      const matches = event && typeof event.matches === 'boolean' ? event.matches : media.matches;
      setSystemTheme(matches ? 'dark' : 'light');
    };

    updateSystemTheme(media);

    if (themePreference !== 'system') {
      return undefined;
    }

    const handler = (event) => updateSystemTheme(event);

    if (typeof media.addEventListener === 'function') {
      media.addEventListener('change', handler);
      return () => media.removeEventListener('change', handler);
    }

    media.addListener(handler);
    return () => media.removeListener(handler);
  }, [themePreference]);

  const appliedTheme = useMemo(() => {
    const resolved = themePreference === 'system' ? systemTheme : themePreference;
    return resolved || 'dark';
  }, [systemTheme, themePreference]);

  useEffect(() => {
    if (typeof document === 'undefined') {
      return undefined;
    }

    const root = document.documentElement;
    const lightClass = 'ocrp-theme-light';
    const darkClass = 'ocrp-theme-dark';

    root.classList.remove(lightClass, darkClass);
    root.classList.add(appliedTheme === 'light' ? lightClass : darkClass);

    if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
      window.dispatchEvent(
        new CustomEvent('ocrp-theme-change', {
          detail: {
            preference: themePreference,
            theme: appliedTheme,
          },
        })
      );
    }

    return undefined;
  }, [appliedTheme, themePreference]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  const handleThemeSelect = (event) => {
    setThemePreference(event.target.value);
  };

  const settingsClasses = ['OCRP-settings'];
  settingsClasses.push(appliedTheme === 'light' ? 'OCRP-settings-light' : 'OCRP-settings-dark');

  return (
    <div className={settingsClasses.join(' ')}>
      {/* Tabs navigation */}
      <div className="OCRP-analysis-tabs">
        <button 
          className={`OCRP-tab-button ${activeTab === 'general' ? 'OCRP-tab-active' : ''}`}
          onClick={() => handleTabChange('general')}
        >
          <i className="fas fa-cog"></i>
          <span>General</span>
        </button>

      </div>
      
      <div className="OCRP-settings-content">
        {activeTab === 'general' && (
          <div className="OCRP-settings-section">
            <h2>General Settings</h2>
            <div className="OCRP-settings-form">
              <div className="OCRP-form-group">
                <label>Application Theme</label>
                <select className="OCRP-select" value={themePreference} onChange={handleThemeSelect}>
                  <option value="dark">Dark (Default)</option>
                  <option value="light">Light</option>
                  <option value="system">System Preference</option>
                </select>
              </div>
              
              <div className="OCRP-form-group">
                <label>Data Refresh Interval</label>
                <select className="OCRP-select">
                  <option value="5">5 minutes</option>
                  <option value="15">15 minutes</option>
                  <option value="30">30 minutes</option>
                  <option value="60">1 hour</option>
                </select>
              </div>
              
            </div>
          </div>
        )}
        
        {activeTab === 'api' && (
          <div className="OCRP-settings-section">
            <h2>API Connections</h2>
            <div className="OCRP-settings-form">
              <div className="OCRP-form-group">
                <label>Default API Endpoint</label>
                <input type="text" className="OCRP-input" value="https://www.phenomeportal.org/oncorisk/about" />
              </div>
              
              <div className="OCRP-form-group">
                <label>API Authentication</label>
                <select className="OCRP-select">
                  <option value="token">Bearer Token</option>
                  <option value="oauth">OAuth 2.0</option>
                  <option value="key">API Key</option>
                </select>
              </div>
              
              <div className="OCRP-form-group">
                <label>API Key / Token</label>
                <div className="OCRP-input-with-button">
                  <input type="password" className="OCRP-input" value="••••••••••••••••••••••••••" />
                  <button className="OCRP-input-button">
                    <i className="fas fa-eye"></i>
                  </button>
                </div>
              </div>
              
              <div className="OCRP-form-group">
                <label>External Integrations</label>
                <div className="OCRP-toggle-group">
                  <div className="OCRP-toggle-item">
                    <input type="checkbox" id="integrate-cbioportal" className="OCRP-checkbox" checked />
                    <label htmlFor="integrate-cbioportal">cBioPortal</label>
                  </div>
                  <div className="OCRP-toggle-item">
                    <input type="checkbox" id="integrate-oncokb" className="OCRP-checkbox" checked />
                    <label htmlFor="integrate-oncokb">OncoKB</label>
                  </div>
                  <div className="OCRP-toggle-item">
                    <input type="checkbox" id="integrate-civic" className="OCRP-checkbox" checked />
                    <label htmlFor="integrate-civic">CIViC</label>
                  </div>
                </div>
              </div>
              
              <div className="OCRP-button-row">
                <button className="OCRP-button OCRP-button-primary">Save Changes</button>
                <button className="OCRP-button OCRP-button-secondary">Test Connection</button>
              </div>
            </div>
          </div>
        )}
        
        {activeTab === 'users' && (
          <div className="OCRP-settings-section">
            <h2>User Access Management</h2>
            <div className="OCRP-settings-form">
              <div className="OCRP-form-group">
                <label>Access Control Mode</label>
                <select className="OCRP-select">
                  <option value="rbac">Role-Based Access Control</option>
                  <option value="abac">Attribute-Based Access Control</option>
                </select>
              </div>
              
              <div className="OCRP-table-container">
                <h3>Active Users</h3>
                <table className="OCRP-table">
                  <thead>
                    <tr>
                      <th>User</th>
                      <th>Role</th>
                      <th>Last Access</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>admin@szapfs.org</td>
                      <td>Administrator</td>
                      <td>2025-05-30 18:45</td>
                      <td><span className="OCRP-status-active">Active</span></td>
                      <td>
                        <button className="OCRP-icon-button">
                          <i className="fas fa-edit"></i>
                        </button>
                        <button className="OCRP-icon-button">
                          <i className="fas fa-key"></i>
                        </button>
                      </td>
                    </tr>
                    <tr>
                      <td>user1@szapfs.org</td>
                      <td>Analyst</td>
                      <td>2025-05-29 14:20</td>
                      <td><span className="OCRP-status-active">Active</span></td>
                      <td>
                        <button className="OCRP-icon-button">
                          <i className="fas fa-edit"></i>
                        </button>
                        <button className="OCRP-icon-button">
                          <i className="fas fa-key"></i>
                        </button>
                      </td>
                    </tr>
                    <tr>
                      <td>user2@szapfs.org</td>
                      <td>Viewer</td>
                      <td>2025-05-28 09:15</td>
                      <td><span className="OCRP-status-inactive">Inactive</span></td>
                      <td>
                        <button className="OCRP-icon-button">
                          <i className="fas fa-edit"></i>
                        </button>
                        <button className="OCRP-icon-button">
                          <i className="fas fa-key"></i>
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              
              <div className="OCRP-button-row">
                <button className="OCRP-button OCRP-button-primary">
                  <i className="fas fa-user-plus"></i>
                  Add New User
                </button>
                <button className="OCRP-button OCRP-button-secondary">
                  <i className="fas fa-sync"></i>
                  Refresh
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Settings;
